/**
 * PrintButton - Square print icon for generating PDF checklists
 *
 * Renders a print icon that generates a PDF from the provided items,
 * including their sub-items and attachments.
 */

import { useState } from 'react';
import { Printer } from 'lucide-react';
import { generateItemsPdf } from '@/utils/generatePdf';
import { getSubItems } from '@perfin/shared';
import { useAuthStore } from '@/stores';
import { useToast } from '@/components/ui/Toast';
import { Spinner } from './Spinner';
import type { Item, SubItem } from '@perfin/shared';

interface PrintButtonProps {
  title: string;
  items: Item[];
  getCategoryName?: (categoryId: string) => string;
  getOwnerName?: (ownerId: string) => string;
}

export function PrintButton({ title, items, getCategoryName, getOwnerName }: PrintButtonProps) {
  const { user } = useAuthStore();
  const toast = useToast();
  const [loading, setLoading] = useState(false);

  const handlePrint = async () => {
    if (!user) {
      toast.error('You must be logged in to print');
      return;
    }

    if (items.length === 0) {
      toast.error('No items to print');
      return;
    }

    setLoading(true);
    try {
      // Fetch sub-items for all items that have sub-items
      const subItemsMap: Record<string, SubItem[]> = {};

      const itemsWithSubItems = items.filter((item) => (item.subItemCount ?? 0) > 0);

      // Fetch sub-items in parallel (use item.createdBy for correct Firestore path)
      const fetchPromises = itemsWithSubItems.map(async (item) => {
        try {
          const subItems = await getSubItems(item.createdBy, item.id);
          subItemsMap[item.id] = subItems;
        } catch (error) {
          console.warn(`Failed to fetch sub-items for item ${item.id}:`, error);
          subItemsMap[item.id] = [];
        }
      });

      await Promise.all(fetchPromises);

      // Generate PDF
      generateItemsPdf({
        title,
        items,
        subItemsMap,
        getCategoryName,
        getOwnerName,
      });

      toast.success('PDF generated');
    } catch (error) {
      console.error('Print error:', error);
      toast.error('Failed to generate PDF');
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      onClick={handlePrint}
      disabled={loading}
      className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors disabled:opacity-50"
      title="Print as PDF checklist"
      aria-label="Print as PDF checklist"
    >
      {loading ? (
        <Spinner size="sm" />
      ) : (
        <Printer className="h-5 w-5" />
      )}
    </button>
  );
}
